import { useEffect, useMemo, useState } from "react";

import { ArrowLeft, Copy, Plus, Search } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useCompanies } from "~/hooks/contabilidad/useCompanies";
import {
  useCopyMappingTemplate,
  useCreateMappingTemplate,
  useDeleteMappingTemplate,
  useMappingTemplates,
  useToggleMappingTemplateActive,
  useUpdateMappingTemplate,
} from "~/hooks/contabilidad/useMappingTemplates";
import type {
  AccountMappingTemplate,
  MappingTemplateFormData,
} from "~/types/contabilidad";

import { MapeoTemplateDialog } from "./components/MapeoTemplateDialog";
import { MapeoTemplateTable } from "./components/MapeoTemplateTable";

const ALIACE_SPA_ID = "809d3197-9371-4cbb-b544-aadbd29940d6";

export default function MapeoCuentasPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [companyFilter, setCompanyFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<
    AccountMappingTemplate | undefined
  >(undefined);

  const { data: companies = [] } = useCompanies();
  const { data: templates = [], isLoading } = useMappingTemplates({
    company_id: companyFilter !== "all" ? companyFilter : undefined,
  });

  // Set default company to Aliace SPA
  useEffect(() => {
    if (companies.length > 0 && companyFilter === "all") {
      const aliaceSpa = companies.find((c) => c.id === ALIACE_SPA_ID);
      if (aliaceSpa) {
        setCompanyFilter(ALIACE_SPA_ID);
      }
    }
  }, [companies]);

  const createMutation = useCreateMappingTemplate();
  const updateMutation = useUpdateMappingTemplate();
  const deleteMutation = useDeleteMappingTemplate();
  const toggleActiveMutation = useToggleMappingTemplateActive();
  const copyMutation = useCopyMappingTemplate();

  const filteredTemplates = useMemo(() => {
    return templates.filter((template) => {
      const matchesSearch =
        !searchQuery ||
        template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.description?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && template.is_active) ||
        (statusFilter === "inactive" && !template.is_active);

      return matchesSearch && matchesStatus;
    });
  }, [templates, searchQuery, statusFilter]);

  const handleCreate = () => {
    setEditingTemplate(undefined);
    setDialogOpen(true);
  };

  const handleEdit = (template: AccountMappingTemplate) => {
    setEditingTemplate(template);
    setDialogOpen(true);
  };

  const handleView = (template: AccountMappingTemplate) => {
    navigate(`/contabilidad/configuracion/mapeo/${template.id}`);
  };

  const handleSubmit = (data: MappingTemplateFormData) => {
    if (editingTemplate) {
      updateMutation.mutate(
        { id: editingTemplate.id, data },
        {
          onSuccess: () => {
            setDialogOpen(false);
            setEditingTemplate(undefined);
          },
        }
      );
    } else {
      createMutation.mutate(data, {
        onSuccess: (newTemplate) => {
          setDialogOpen(false);
          navigate(`/contabilidad/configuracion/mapeo/${newTemplate.id}`);
        },
      });
    }
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleToggleActive = (id: string, isActive: boolean) => {
    toggleActiveMutation.mutate({ id, isActive });
  };

  const handleCopy = (template: AccountMappingTemplate) => {
    const newName = `${template.name} (Copia)`;
    copyMutation.mutate(
      {
        sourceTemplateId: template.id,
        newName,
        companyId: template.company_id,
      },
      {
        onSuccess: (newTemplate) => {
          navigate(`/contabilidad/configuracion/mapeo/${newTemplate.id}`);
        },
      }
    );
  };

  return (
    <div className="container mx-auto w-[80vw] space-y-6 py-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/contabilidad/configuracion">
            <Button variant="ghost" size="icon" className="rounded-full">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold">Mapeo de Cuentas</h1>
            <p className="text-sm text-muted-foreground">
              Plantillas de mapeo para estados financieros
            </p>
          </div>
        </div>
        <Button onClick={handleCreate} className="rounded-full">
          <Plus className="mr-2 h-4 w-4" />
          Nueva Plantilla
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-4 pt-4">
            <div className="relative min-w-[200px] max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 rounded-xl pl-9"
              />
            </div>
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger className="h-9 w-[200px] rounded-xl">
                <SelectValue placeholder="Filtrar por empresa" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las empresas</SelectItem>
                {companies.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9 w-[140px] rounded-xl">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="active">Activas</SelectItem>
                <SelectItem value="inactive">Inactivas</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <MapeoTemplateTable
            templates={filteredTemplates}
            isLoading={isLoading}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onToggleActive={handleToggleActive}
            onCopy={handleCopy}
          />
        </CardContent>
      </Card>

      <MapeoTemplateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        template={editingTemplate}
        onSubmit={handleSubmit}
        isLoading={createMutation.isPending || updateMutation.isPending}
        defaultCompanyId={companyFilter !== "all" ? companyFilter : ALIACE_SPA_ID}
      />
    </div>
  );
}
